import { alpha, Box, CircularProgress, Typography, useTheme } from '@mui/material'
import { IpqsEmailDetail, IpqsPhoneDetail } from '@src/data/types/FraudAnalysisResult'
import { useTranslation } from 'react-i18next'

interface IPQSDetailsProps {
  type: 'email' | 'phone'
  emailDetail?: IpqsEmailDetail | null
  phoneDetail?: IpqsPhoneDetail | null
  region?: string
  loading?: boolean
  analysisMessage?: string
}

export const IPQSDetails: React.FC<IPQSDetailsProps> = ({ type, analysisMessage, emailDetail, phoneDetail, region, loading }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  if (loading) {
    return <CircularProgress size={16} />
  }

  const data = type === 'email' ? emailDetail : phoneDetail

  if (!data) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {t('fraudFlags.ipqs.noDataAvailable', 'No detailed data available')}
      </Typography>
    )
  }

  const formatValue = (value: string | number | boolean | undefined | null, label: string): string => {
    if (value === undefined || value === null || value === '') {
      return `${label}: ${t('fraudFlags.ipqs.notAvailable', 'N/A')}`
    }

    if (typeof value === 'boolean') {
      return `${label}: ${value ? t('fraudFlags.ipqs.yes', 'Yes') : t('fraudFlags.ipqs.no', 'No')}`
    }

    return `${label}: ${value}`
  }

  const details = type === 'email' && emailDetail
    ? [
        formatValue(emailDetail.valid, t('fraudFlags.ipqs.valid', 'Valid')),
        formatValue(emailDetail.disposable, t('fraudFlags.ipqs.disposable', 'Disposable')),
        formatValue(emailDetail.spamTrapScore, t('fraudFlags.ipqs.spamTrapScore', 'Spam Trap Score')),
        formatValue(emailDetail.domainTrust, t('fraudFlags.ipqs.domainTrust', 'Domain Trust')),
        formatValue(emailDetail.suspect, t('fraudFlags.ipqs.suspect', 'Suspect')),
        formatValue(emailDetail.recentAbuse, t('fraudFlags.ipqs.recentAbuse', 'Recent Abuse')),
      ]
    : phoneDetail
      ? [
          formatValue(phoneDetail.valid, t('fraudFlags.ipqs.valid', 'Valid')),
          formatValue(phoneDetail.region || region, t('fraudFlags.ipqs.region', 'Region')),
          formatValue(phoneDetail.active, t('fraudFlags.ipqs.active', 'Active')),
          formatValue(phoneDetail.lineType, t('fraudFlags.ipqs.lineType', 'Line Type')),
          formatValue(phoneDetail.carrier, t('fraudFlags.ipqs.carrier', 'Carrier')),
        ]
      : []

  const messageDetail = formatValue(data.message ?? analysisMessage, t('fraudFlags.ipqs.message', 'Message'))

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      <Box
        sx={{
          px: 1, py: 0.5, mb: 0.5, borderRadius: 1,
          backgroundColor: alpha(theme.palette.info.main, 0.1),
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 28, width: '100%',
        }}
      >
        <Typography variant="caption" sx={{ color: theme.palette.info.dark, fontWeight: 500, textAlign: 'center', fontSize: '0.7rem' }}>
          {messageDetail}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, width: '100%' }}>
        {details.map((detail, index) => (
          <Box
            key={index}
            sx={{
              px: 1, py: 0.5, borderRadius: 1,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 28,
            }}
          >
            <Typography variant="caption" sx={{ color: theme.palette.info.dark, fontWeight: 500, textAlign: 'center', fontSize: '0.7rem' }}>
              {detail}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
